import { getAllRequiredFields } from "./isRequired";
import { FlattenedRoom, KeyofRoom } from "@pages/Assets/RoomProvisioning/const";
import { Room } from "@services/rooms/types";
import {
  GenericFieldError,
  testOneKey,
} from "@common/validation/entityValidationUtils";
import { KeyofLocation } from "@pages/Assets/LocationProvisioning/const";
import { getDefaultMinMaxDates } from "@common/utils/localTime.utils";
import { IFieldValueContext } from "@pages/Assets/Rooms/RoomDrawer/Content/context/fieldValue";
import { getAllFieldsInError as locationGetAllFieldsInError } from "@common/utils/locations/validation";

export const getErrorFromLocationFields = (
  keys: KeyofLocation[]
): KeyofRoom[] => keys.map((k) => `location.${k}` as KeyofRoom);

export const getExpirationDateErrors = (room: Room): FieldError[] => {
  const { minDate, maxDate } = getDefaultMinMaxDates;
  let temp: FieldError[] = [];
  if (
    room.details?.serviceExpirationDate !== undefined &&
    (room.details.serviceExpirationDate < minDate.getTime() ||
      room.details.serviceExpirationDate > maxDate.getTime() ||
      isNaN(room?.details?.serviceExpirationDate))
  ) {
    temp.push({
      property: "details.serviceExpirationDate",
      error: RoomPropertyErrors.INVALID_DATE,
    });
  }
  if (
    room.details?.certificationDate !== undefined &&
    room.details?.certificationDate !== null &&
    (room.details.certificationDate < minDate.getTime() ||
      room.details.certificationDate > maxDate.getTime() ||
      isNaN(room.details.certificationDate))
  ) {
    temp.push({
      property: "details.certificationDate",
      error: RoomPropertyErrors.INVALID_DATE,
    });
  }

  return temp;
};

export const getRoomTypesFieldErrors = (
  room: Room,
  values: IFieldValueContext["roomTypes"] = []
): FieldError[] => {
  let temp: FieldError[] = [];
  if (
    !values.some((v) => v.name == room.details?.roomType) &&
    room.details?.roomType != undefined &&
    room.details?.roomType != ""
  ) {
    temp.push({
      property: "details.roomType",
      error: RoomPropertyErrors.ROOM_TYPE_INVALID_OR_EMPTY,
    });
  }
  return temp;
};

export const getEmptyRequiredFields = (
  room: Partial<Room>,
  includeLocationFields: boolean = false
): FieldError[] =>
  getAllRequiredFields(room, includeLocationFields)
    .filter((x) => !testOneKey<Room, FlattenedRoom>(room, x))
    .map((property) => ({
      property,
      error: RoomPropertyErrors.REQUIRED_FIELD_IS_EMPTY,
    }));
export const areRequiredFieldFilled = (
  room: Partial<Room>,
  includeLocationFields: boolean = false
) => getEmptyRequiredFields(room, includeLocationFields).length === 0;

const getRoomLocationErrors = (room: Partial<Room>): FieldError[] => {
  if (room.location !== undefined) {
    return locationGetAllFieldsInError(room.location).map((location) => ({
      property: `location.${location.property}` as KeyofRoom,
      error: location.error as unknown as RoomPropertyErrors,
    }));
  }

  return [];
};

export enum RoomPropertyErrors {
  REQUIRED_FIELD_IS_EMPTY = "This field is required.",
  INVALID_DATE = "Please select a valid date.",
  ROOM_TYPE_INVALID_OR_EMPTY = "Please select a valid room type.",
  LOCATION_ID = "The Location ID should match the Location Name",
  INVALID_LONGITUDE = "The longitude is invalid.",
  INVALID_LATITUDE = "The latitude is invalid.",
}
export type FieldError = GenericFieldError<
  keyof FlattenedRoom,
  RoomPropertyErrors
>;

export const getAllFieldsInError = (
  room: Partial<Room>,
  includeLocationFields: boolean = false,
  roomTypesValues: IFieldValueContext["roomTypes"] = undefined
): FieldError[] => {
  return [
    getEmptyRequiredFields(room, includeLocationFields),
    getExpirationDateErrors(room),
    getRoomTypesFieldErrors(room, roomTypesValues),
    includeLocationFields ? getRoomLocationErrors(room) : [],
  ].flat();
};
